/**
 * Pay-change audit lookup — answers "who approved Jimmy Jack at 235/day?"
 *
 * Thin surface around GET /api/finance/audit/pay-changes?employee_id=…
 * The user picks an employee from the existing directory read model; the
 * returned rows show every classified adjustment with approver, date, run,
 * classification, and reason. One-call forensic tool.
 */

import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { fetchPayChangeAudit, type PayChangeAuditRow } from './use-payroll-workbook.js';
import { useDirectoryRead } from '../directory/use-directory-read.js';

interface EmpBrief {
  id: string;
  first_name?: string;
  last_name?: string;
  driver_id?: string;
}

export default function PayrollAuditLookup() {
  const { employees } = useDirectoryRead();
  const empList = (employees as EmpBrief[]) || [];
  const [selected, setSelected] = useState<string>('');
  const [result, setResult] = useState<PayChangeAuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleLookup(employeeId: string) {
    setSelected(employeeId);
    setResult([]);
    setErr(null);
    if (!employeeId) return;
    setLoading(true);
    try {
      const resp = await fetchPayChangeAudit(employeeId);
      if (resp?.error) setErr(resp.error);
      else setResult(resp?.adjustments || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
        Pay-change audit
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
        Answers “who approved this employee at this rate, when, in which payroll run.”
      </div>
      <select
        value={selected}
        onChange={(e) => handleLookup(e.target.value)}
        style={{
          width: '100%', maxWidth: 360, padding: '6px 10px', fontSize: 13,
          background: C.panel, color: C.text,
          border: `1px solid ${C.borderSoft}`, borderRadius: 6,
        }}
      >
        <option value="">— Select employee —</option>
        {empList
          .slice()
          .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
          .map((e) => (
            <option key={e.id} value={e.id}>
              {e.last_name}, {e.first_name}{e.driver_id ? ` · ${e.driver_id}` : ''}
            </option>
          ))}
      </select>

      {loading && <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted }}>Loading…</div>}
      {err && <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>{err}</div>}

      {selected && !loading && result.length === 0 && !err && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.textMuted }}>
          No pay-change adjustments recorded for this employee.
        </div>
      )}

      {result.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          {result.map((a) => (
            <div key={a.adjustment_id} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, fontSize: 12 }}>
              <div style={{ color: C.text }}>
                <span style={{ fontFamily: 'DM Mono, monospace' }}>{a.field_name}</span>
                : {a.original_value || '—'} → <span style={{ fontWeight: 700 }}>{a.corrected_value}</span>
                <span style={{ marginLeft: 10, fontSize: 10, padding: '2px 8px', borderRadius: 999, background: `${C.gold}22`, color: C.gold, textTransform: 'uppercase' }}>
                  {a.classification}
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                {a.week_ending ? `week ${a.week_ending}` : 'no run'}
                {a.effective_date && ` · effective ${a.effective_date}`}
                {a.run_status && ` · run ${a.run_status}`}
                {a.reason && ` · ${a.reason}`}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted }}>
                Approved by <span style={{ color: C.text }}>{a.approved_by_email}</span> on{' '}
                {new Date(a.approved_at).toLocaleString()}
                {a.confirmed && <span style={{ marginLeft: 6, color: C.green }}>· confirmed</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
